import React, { useEffect, useState, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { Table, Input, Tooltip } from "antd";
import { format } from "date-fns";
import {
  Currency,
  callNonStoreApi,
  formatPrice,
  isNullOrUndefined,
} from "../utils/utils";
import {
  GetGrowezyClientPayoutSummaryDetails,
  ManuallyDoReadyToProcess,
  GetOrders,
} from "../utils/ApiEndpoints";
import ButtonWithAlertDialog from "../Payouts/ButtonWithAlertDialog";
import { enqueueSnackbar } from "notistack";
import { Parser } from "@json2csv/plainjs";
import { Download, ArrowForward } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Design tokens
const colors = {
  bg: "#fafbfc",
  surface: "#ffffff",
  surfaceHover: "#f8fafc",
  border: "#e2e8f0",
  borderLight: "#f1f5f9",
  text: "#0f172a",
  textSecondary: "#475569",
  textMuted: "#94a3b8",
  accent: "#6366f1",
  accentHover: "#4f46e5",
  accentLight: "#eef2ff",
  warning: "#ea580c",
  warningBg: "#fff7ed",
  warningBorder: "#fed7aa",
};

const PageContainer = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: ${colors.bg};
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  -webkit-font-smoothing: antialiased;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  height: 64px;
  background: ${colors.surface};
  border-bottom: 1px solid ${colors.border};
  flex-shrink: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: ${colors.warningBg};
  border: 1px solid ${colors.warningBorder};
  border-radius: 8px;
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${colors.warning};
`;

const PageTitle = styled.h1`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.text};
  margin: 0;
  letter-spacing: -0.01em;
`;

const StatsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

const StatLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const StatValue = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.text};
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  height: 52px;
  background: ${colors.surface};
  border-bottom: 1px solid ${colors.borderLight};
  flex-shrink: 0;
`;

const RecordCount = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.textSecondary};
`;

const SearchInput = styled(Input)`
  width: 240px;

  &.ant-input-affix-wrapper {
    background: ${colors.bg};
    border-radius: 8px;
    border: 1px solid ${colors.border};
    padding: 8px 12px;
    font-size: 13px;
    transition: all 0.15s ease;

    &:hover {
      border-color: ${colors.textMuted};
    }

    &:focus,
    &.ant-input-affix-wrapper-focused {
      border-color: ${colors.accent};
      box-shadow: 0 0 0 3px ${colors.accentLight};
    }

    .ant-input {
      background: transparent;
      font-size: 13px;

      &::placeholder {
        color: ${colors.textMuted};
      }
    }
  }
`;

const TableContainer = styled.div`
  flex: 1;
  overflow: hidden;
  animation: ${fadeIn} 0.3s ease;

  .ant-table-wrapper,
  .ant-spin-nested-loading,
  .ant-spin-container,
  .ant-table {
    height: 100%;
  }

  .ant-table-container {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .ant-table-header {
    flex-shrink: 0;
  }

  .ant-table-body {
    flex: 1;
    overflow-y: auto !important;
  }

  .ant-table-thead > tr > th {
    background: ${colors.surface};
    color: ${colors.textMuted};
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 14px 32px;
    border-bottom: 1px solid ${colors.border};

    &::before {
      display: none !important;
    }

    &.ant-table-column-has-sorters:hover {
      background: ${colors.surfaceHover};
    }
  }

  .ant-table-tbody > tr {
    transition: background 0.1s ease;

    &:hover > td {
      background: ${colors.surfaceHover} !important;
    }

    > td {
      padding: 16px 32px;
      color: ${colors.text};
      border-bottom: 1px solid ${colors.borderLight};
      font-size: 13px;
      vertical-align: middle;
    }

    &:last-child > td {
      border-bottom: none;
    }
  }

  .ant-pagination {
    padding: 16px 32px;
    margin: 0;
    background: ${colors.surface};
    border-top: 1px solid ${colors.border};
  }
`;

const ClientName = styled.span`
  font-weight: 500;
  color: ${colors.text};
`;

const OrdersCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  background: ${colors.warningBg};
  color: ${colors.warning};
  border: 1px solid ${colors.warningBorder};
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
`;

const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: ${colors.surface};
  border: 1px solid ${colors.border};
  border-radius: 6px;
  color: ${colors.textMuted};
  cursor: pointer;
  padding: 0;
  transition: all 0.15s ease;

  &:hover {
    background: ${colors.accentLight};
    border-color: ${colors.accent};
    color: ${colors.accent};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    &:hover {
      background: ${colors.surface};
      border-color: ${colors.border};
      color: ${colors.textMuted};
    }
  }
`;

const Amount = styled.span`
  font-weight: 600;
  color: ${colors.text};
  font-variant-numeric: tabular-nums;
  font-size: 13px;
`;

const SecondaryText = styled.span`
  color: ${colors.textMuted};
  font-size: 13px;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: ${colors.text};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #000;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    font-size: 14px;
  }
`;

const SearchIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 16 16"
    fill="none"
    stroke={colors.textMuted}
    strokeWidth="1.5"
  >
    <circle cx="7" cy="7" r="5" />
    <path d="M11 11l3.5 3.5" />
  </svg>
);

const LoadingContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: ${colors.textMuted};
  font-size: 13px;
  font-weight: 500;
`;

const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border: 2px solid ${colors.borderLight};
  border-top-color: ${colors.accent};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const EmptyContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: ${colors.textMuted};
`;

const EmptyText = styled.span`
  font-size: 14px;
  font-weight: 500;
`;

const EmptySubtext = styled.span`
  font-size: 13px;
  color: ${colors.textMuted};
`;

function SettlementsPending() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);

  const fetchData = async () => {
    try {
      const res = await callNonStoreApi(GetGrowezyClientPayoutSummaryDetails);
      setData(res.data.docs || []);
    } catch (error) {
      enqueueSnackbar("Failed to load data", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDownloadCSV = async (record, e) => {
    e.stopPropagation();
    setDownloadingId(record.userId);

    try {
      const response = await callNonStoreApi(GetOrders, {
        ordersList: record.pending?.orders,
        userId: record.userId,
      });

      const orders = response.data.orders || [];
      if (orders.length === 0) {
        enqueueSnackbar("No orders to export", { variant: "warning" });
        return;
      }

      const list = orders.map((d) => ({
        Customer: d.customerName,
        "Offering Name": d.offeringName,
        "Order Id": d.orderId,
        "Booking Date": format(new Date(d.date), "dd MMM yyyy, HH:mm"),
        "Cart Value": formatPrice(
          d.paymentBreakdown?.cartValue || 0,
          Currency.INR,
          "code"
        ),
        "Amount Paid": formatPrice(
          d.paymentBreakdown?.totalAmount || 0,
          Currency.INR,
          "code"
        ),
        "Platform Fee": formatPrice(
          d.paymentBreakdown?.platformFee || 0,
          Currency.INR,
          "code"
        ),
        "PG Charges": formatPrice(
          d.paymentBreakdown?.convenience || 0,
          Currency.INR,
          "code"
        ),
        "Total Receivable": formatPrice(
          d.payoutAmount || 0,
          Currency.INR,
          "code"
        ),
      }));

      const parser = new Parser();
      const csv = parser.parse(list);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `pending_${record.username}_${format(
        new Date(),
        "yyyy-MM-dd"
      )}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      enqueueSnackbar("Downloaded successfully", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Download failed", { variant: "error" });
    } finally {
      setDownloadingId(null);
    }
  };

  const filteredData = useMemo(() => {
    if (!searchText.trim()) return data;
    return data.filter((item) =>
      item.username?.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [data, searchText]);

  const stats = useMemo(() => {
    return data.reduce(
      (acc, item) => ({
        clients: acc.clients + 1,
        orders: acc.orders + (item.pending?.total || 0),
        amount: acc.amount + (item.pending?.totalAmount || 0),
      }),
      { clients: 0, orders: 0, amount: 0 }
    );
  }, [data]);

  const columns = [
    {
      title: "Client",
      dataIndex: "username",
      key: "username",
      render: (text) => <ClientName>{text}</ClientName>,
      sorter: (a, b) => a.username?.localeCompare(b.username),
    },
    {
      title: "Pending Orders",
      dataIndex: "pending",
      key: "pending",
      render: (pending, record) => (
        <OrdersCell>
          <Badge>{pending?.total || 0} orders</Badge>
          <Tooltip title="Export to CSV">
            <IconBtn
              onClick={(e) => handleDownloadCSV(record, e)}
              disabled={downloadingId === record.userId}
            >
              {downloadingId === record.userId ? (
                <CircularProgress size={12} sx={{ color: colors.textMuted }} />
              ) : (
                <Download sx={{ fontSize: 14 }} />
              )}
            </IconBtn>
          </Tooltip>
        </OrdersCell>
      ),
    },
    {
      title: "Pending Amount",
      dataIndex: "pending",
      key: "amount",
      render: (pending) => (
        <Amount>{formatPrice(pending?.totalAmount || 0, Currency.INR)}</Amount>
      ),
    },
    {
      title: "Last Settled",
      dataIndex: "latestDetails",
      key: "lastSettled",
      render: (details) => (
        <SecondaryText>
          {isNullOrUndefined(details?.settlements)
            ? "—"
            : format(
                new Date(details.settlements.latestSettlementDate),
                "MMM d, yyyy"
              )}
        </SecondaryText>
      ),
    },
    {
      title: "Total Settled",
      dataIndex: "totalSettled",
      key: "totalSettled",
      render: (amount) => (
        <SecondaryText>{formatPrice(amount || 0, Currency.INR)}</SecondaryText>
      ),
    },
    {
      title: "",
      dataIndex: "userId",
      key: "actions",
      width: 140,
      align: "right",
      render: (userId) => (
        <ButtonWithAlertDialog
          component={
            <ActionButton>
              Process <ArrowForward sx={{ fontSize: 14 }} />
            </ActionButton>
          }
          buttons={[
            {
              title: "Confirm",
              action: async () => {
                try {
                  await callNonStoreApi(ManuallyDoReadyToProcess, { userId });
                  enqueueSnackbar("Moved to Ready", { variant: "success" });
                  fetchData();
                } catch {
                  enqueueSnackbar("Failed", { variant: "error" });
                }
              },
            },
          ]}
          title="Move to Ready to Process?"
        />
      ),
    },
  ];

  if (loading) {
    return (
      <PageContainer>
        <Header>
          <HeaderLeft>
            <StatusIndicator>
              <StatusDot />
            </StatusIndicator>
            <PageTitle>Pending Settlements</PageTitle>
          </HeaderLeft>
        </Header>
        <LoadingContainer>
          <Spinner />
          <span>Loading settlements...</span>
        </LoadingContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <HeaderLeft>
          <StatusIndicator>
            <StatusDot />
          </StatusIndicator>
          <PageTitle>Pending Settlements</PageTitle>
        </HeaderLeft>
        <StatsGroup>
          <StatItem>
            <StatLabel>Clients</StatLabel>
            <StatValue>{stats.clients}</StatValue>
          </StatItem>
          <StatItem>
            <StatLabel>Orders</StatLabel>
            <StatValue>{stats.orders}</StatValue>
          </StatItem>
          <StatItem>
            <StatLabel>Total Value</StatLabel>
            <StatValue>{formatPrice(stats.amount, Currency.INR)}</StatValue>
          </StatItem>
        </StatsGroup>
      </Header>

      <Toolbar>
        <RecordCount>
          {filteredData.length}{" "}
          {filteredData.length === 1 ? "client" : "clients"}
        </RecordCount>
        <SearchInput
          placeholder="Search clients..."
          prefix={<SearchIcon />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
      </Toolbar>

      {filteredData.length === 0 ? (
        <EmptyContainer>
          <EmptyText>
            {searchText ? "No matching clients" : "No pending settlements"}
          </EmptyText>
          <EmptySubtext>
            {searchText ? "Try a different search term" : "All caught up!"}
          </EmptySubtext>
        </EmptyContainer>
      ) : (
        <TableContainer>
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey="userId"
            pagination={{ pageSize: 20, showSizeChanger: false, size: "small" }}
            scroll={{ y: "calc(100vh - 170px)" }}
          />
        </TableContainer>
      )}
    </PageContainer>
  );
}

export default SettlementsPending;
